import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, Trophy, Zap } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LanguageContext';
import { createTextPicker, getLocalizedField } from '../../utils/localizedContent';

const TCG_COLORS = { pokemon: '#D4A84B', magic: '#0D5E4A', yugioh: '#C45B4A', onepiece: '#EF4444', riftbound: '#0BC4E2', board_game: '#2D8B70', all: '#C9A962', dbs: '#C45B4A' };
const TYPE_LABEL_KEYS = {
  tournament: 'events.type_tournament',
  casual: 'events.type_casual',
  draft: 'events.type_draft',
  sealed: 'events.type_sealed',
  league: 'events.type_league',
};

export default function EventsListPage({ pageType }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTcg, setFilterTcg] = useState('');
  const [regLoading, setRegLoading] = useState('');
  const [message, setMessage] = useState({});
  const { user } = useAuth();
  const { t, lang } = useLang();

  const isTournaments = pageType === 'tournament';
  const l = createTextPicker(lang);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { event_type: pageType };
      if (filterTcg) params.tcg_type = filterTcg;
      const { data } = await axios.get(`${API}/events`, { params });
      setEvents(data || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
    setLoading(false);
  }, [filterTcg, pageType]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleRegister = async (eventId, isRegistered) => {
    if (!user) { window.location.href = '/auth'; return; }
    setRegLoading(eventId);
    try {
      if (isRegistered) {
        await axios.delete(`${API}/events/${eventId}/register`);
        setMessage(m => ({ ...m, [eventId]: l('Inscripción cancelada', 'Registration cancelled') }));
      } else {
        await axios.post(`${API}/events/${eventId}/register`);
        setMessage(m => ({ ...m, [eventId]: l('¡Inscrito!', 'Registered!') }));
      }
      fetchEvents();
    } catch (e) {
      setMessage(m => ({ ...m, [eventId]: e.response?.data?.detail || 'Error' }));
    }
    setRegLoading('');
    setTimeout(() => setMessage(m => ({ ...m, [eventId]: '' })), 3000);
  };

  const tcgFilters = [
    { key: '', label: l('Todos', 'All') },
    { key: 'pokemon', label: 'Pokémon' },
    { key: 'magic', label: 'Magic' },
    { key: 'yugioh', label: 'Yu-Gi-Oh!' },
    { key: 'onepiece', label: 'One Piece' },
    { key: 'riftbound', label: 'Riftbound' },
    { key: 'board_game', label: l('Juegos de Mesa', 'Board Games') },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-black text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
          {isTournaments ? t('events.tournaments_title') : t('events.title')}
        </h1>
      </div>
      <p className="text-[#A8B5AE] mb-6">
        {isTournaments ? t('events.tournaments_subtitle') : t('events.events_subtitle')}
      </p>

      <div className="flex flex-wrap gap-2 mb-8">
        {tcgFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilterTcg(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${filterTcg === f.key ? 'bg-[#0D5E4A] text-[#F5F0E6]' : 'bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6]'}`}
            data-testid={`filter-tcg-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(4)].map((_, i) => <div key={`event-skeleton-${i}`} className="card-glass h-64 skeleton"></div>)}
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 text-[#A8B5AE]">
          <Trophy size={48} className="mx-auto mb-4 opacity-30" />
          <p>{isTournaments ? t('events.empty_tournaments') : t('events.empty_events')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => {
            const isRegistered = user && event.registered_participants?.includes(user.user_id);
            const isFull = event.registered_participants?.length >= event.max_participants;
            const color = TCG_COLORS[event.tcg_type] || '#C9A962';
            const typeLabel = t(TYPE_LABEL_KEYS[event.event_type] || 'events.tournament');
            const title = getLocalizedField(event, 'title', lang, event.title);
            const description = getLocalizedField(event, 'description', lang, event.description);
            const prize = getLocalizedField(event, 'prize', lang, event.prize);
            return (
              <div key={event.event_id} className="card-glass overflow-hidden flex flex-col" data-testid={`event-${event.event_id}`}>
                <Link to={`/events/${event.event_id}`} className="block">
                  {event.image && (
                    <div className="h-44 overflow-hidden relative">
                      <img src={event.image} alt={title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1612]/90 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 flex gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>{typeLabel}</span>
                        {event.entry_fee === 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#2D8B70]/20 text-[#4ADE80] border border-[#2D8B70]/40">{t('events.free')}</span>}
                      </div>
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-[#F5F0E6] font-bold text-base mb-2 leading-snug hover:text-[#C9A962] transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>
                      {title}
                    </h3>
                    <p className="text-[#A8B5AE] text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
                      {description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-[#A8B5AE] mb-4">
                      <span className="flex items-center gap-1"><Calendar size={12} />{event.date}</span>
                      <span className="flex items-center gap-1"><Clock size={12} />{event.time}</span>
                      <span className="flex items-center gap-1"><Users size={12} />{event.registered_participants?.length || 0}/{event.max_participants}</span>
                    </div>
                    {event.prize && <div className="flex items-center gap-2 text-xs mb-3"><Trophy size={12} className="text-[#C9A962]" /><span className="text-[#A8B5AE]">{t('events.prize')}: <span className="text-[#F5F0E6]">{prize}</span></span></div>}
                    {event.entry_fee > 0 && <div className="flex items-center gap-2 text-xs mb-3"><Zap size={12} className="text-[#C9A962]" /><span className="text-[#A8B5AE]">{t('events.entry_fee')}: <span className="text-[#C9A962] font-bold">{event.entry_fee?.toFixed(2)}€</span></span></div>}
                  </div>
                </Link>
                <div className="px-5 pb-5">
                  {message[event.event_id] && <p className="text-xs text-[#2D8B70] mb-2">{message[event.event_id]}</p>}
                  {event.allow_registration !== false && (
                    <button
                      onClick={() => handleRegister(event.event_id, isRegistered)}
                      disabled={regLoading === event.event_id || (isFull && !isRegistered)}
                      className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 ${isRegistered ? 'bg-[#C45B4A]/20 text-[#F87171] hover:bg-[#C45B4A]/30 border border-[#C45B4A]/20' : isFull ? 'bg-[#A8B5AE]/20 text-[#A8B5AE] cursor-not-allowed' : 'btn-primary'}`}
                      data-testid={`register-btn-${event.event_id}`}
                    >
                      {regLoading === event.event_id ? '...' : isRegistered ? t('events.unregister') : isFull ? t('events.full') : t('events.register')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
